import React, { useState } from 'react'
import Blocks from './blocks'
import Redux from 'app/interface/redux'
import Connect from 'app/redux/connect'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core'
import { Alert } from '@material-ui/lab'

interface Props extends Redux {
  onSubmit: (forceSaveWithFbErrors?: boolean) => void
}

const Review = ({
  pages: {
    business: {
      main,
      funnel: { stages },
    },
  },
  core: { errors },
  onSubmit,
}: Props) => {
  const [confirmSaveAnywayOpen, setConfirmSaveAnywayOpen] = useState(false)

  // only offer "Save Anyway" when every current error is an FB access failure that's safe to bypass —
  // a single blocking (non-FB) error alongside it still means the save cannot go through at all
  const canSaveAnyway = errors.length > 0 && errors.every(e => e.bypassable)

  return (
    <div>
      <h2>Review Your Business</h2>
      <p>Please ensure that all of the information is valid before saving.</p>
      <hr />
      <div className="kt-form__section kt-form__section--first">
        {errors.length === 0 ? (
          <div>
            <Blocks.Main main={main} />
            <Blocks.Funnel stages={stages} />
          </div>
        ) : (
          <div className="alert alert-danger" role="alert">
            <div className="alert-text">
              <h4 className="alert-heading">There's a few problems</h4>
              <p>Your business couldn't be saved due to a few problems.</p>
              <hr />
              <ul>
                {errors.map(({ message, column }, i) =>
                  message.length > 0 ? (
                    <li key={i}>
                      {message}
                      {column && ` (${column})`}
                    </li>
                  ) : null
                )}
              </ul>
              {canSaveAnyway && (
                <p className="mb-0">
                  These are Facebook access issues only. You can save the business now and resolve
                  them later from the Error Center.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="kt-form__actions">
        <div className="text-right">
          {canSaveAnyway && (
            <div
              className="btn btn-secondary btn-md btn-tall btn-wide kt-font-bold kt-font-transform-u mr-2"
              onClick={() => setConfirmSaveAnywayOpen(true)}
            >
              Save Anyway
            </div>
          )}
          <div
            className="btn btn-primary btn-md btn-tall btn-wide kt-font-bold kt-font-transform-u"
            onClick={() => onSubmit()}
          >
            Save Business
          </div>
        </div>
      </div>

      <Dialog
        open={confirmSaveAnywayOpen}
        onClose={() => setConfirmSaveAnywayOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Save with Facebook Access Errors?</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Facebook data (ad performance, leads, uploads, etc.) will <strong>stop syncing</strong>{' '}
            for this business until the access issue is resolved. The business will save now, and
            the issue will show up in the Error Center so it can be fixed later.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSaveAnywayOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setConfirmSaveAnywayOpen(false)
              onSubmit(true)
            }}
            color="secondary"
            variant="contained"
          >
            Save Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default Connect(Review)
